// RECIPETABLE1 (docs/build-briefs/RECIPETABLE1-recipes-table.md; design/style-guide.md sections 7, 8, 10, 12). The pick-one sheet of the
// Settings > Recipes bulk strip, one sheet for the four fields (Display category, Active / Inactive, Requires liquor, Build charge). It
// has the steps and look of the inventory list's Set vendor sheet (bulkEditSheet.ts): pick, confirm, apply, result, built at runtime
// and appended to the page (check:chrome-ratchet refuses a new scrim-shaped overlay in index.html), the same bv-* styles. The caller
// owns the call (apply a batch); this draws the steps. Names go in with textContent only. Sign-out closes it through
// closeBulkRecipeSheet().

import type { BulkMenuDrinkResultDto } from "./api.ts";
import { parseDollarsToCentsExact } from "./money.ts";
import {
  BULK_RECIPE_BATCH_SIZE,
  BULK_RECIPE_COPY,
  addBatch,
  applyButtonLabel,
  confirmSentence,
  emptyTotals,
  resultSentence,
  sheetTitle,
  stoppedSentence,
  toBatches,
  type BulkRecipeChoice,
  type BulkRecipeField,
  type BulkRecipeTotals,
} from "./recipeBulkModel.ts";

export interface BulkRecipeOption {
  value: string;
  label: string;
}

export interface BulkRecipeDrink {
  id: string;
  name: string;
}

/** What the caller sends for one batch: the choice as the route's body wants it. */
export type BulkRecipeRequest =
  | { field: "category"; category: string }
  | { field: "active"; active: boolean }
  | { field: "requiresLiquor"; requiresLiquorSelection: false }
  | { field: "requiresLiquor"; requiresLiquorSelection: true; liquorSlots: string[] }
  | { field: "buildCharge"; buildChargeCents: number };

export type BulkRecipeApply =
  | { ok: true; result: BulkMenuDrinkResultDto }
  | { ok: false; message: string };

export interface BulkRecipeSheetOptions {
  field: BulkRecipeField;
  drinks: readonly BulkRecipeDrink[];
  /** The display categories (field "category") or the liquor categories (field "requiresLiquor"), A to Z. */
  options: readonly BulkRecipeOption[];
  applyBatch: (request: BulkRecipeRequest, drinkIds: string[]) => Promise<BulkRecipeApply>;
  /** Called when the sheet closes. `failedIds` are the drinks that could not be changed (they stay selected); `touched` is true
   * when any batch ran, so the caller reloads the list. */
  onClosed: (outcome: { touched: boolean; failedIds: string[] }) => void;
}

const SCRIM_ID = "bulk-recipe-scrim";

let scrim: HTMLElement | undefined;
let generation = 0;
let closeOutcome: { touched: boolean; failedIds: string[] } = { touched: false, failedIds: [] };
let onClosedCallback: BulkRecipeSheetOptions["onClosed"] | undefined;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
  testId?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  if (testId !== undefined) node.dataset["testid"] = testId;
  return node;
}

export function isBulkRecipeSheetOpen(): boolean {
  return scrim !== undefined;
}

/** Closes and removes the sheet. Safe when it was never opened. */
export function closeBulkRecipeSheet(): void {
  if (scrim === undefined) return;
  generation += 1;
  scrim.remove();
  scrim = undefined;
  const after = onClosedCallback;
  const outcome = closeOutcome;
  onClosedCallback = undefined;
  closeOutcome = { touched: false, failedIds: [] };
  after?.(outcome);
}

export function openBulkRecipeSheet(opts: BulkRecipeSheetOptions): void {
  scrim?.remove();
  generation += 1;
  const mine = generation;
  closeOutcome = { touched: false, failedIds: [] };
  onClosedCallback = opts.onClosed;
  const count = opts.drinks.length;
  const field = opts.field;

  const root = el("div", "scrim", undefined, SCRIM_ID);
  root.id = SCRIM_ID;
  const sheet = el("div", "sheet bulk-vendor-sheet", undefined, "bulk-recipe-sheet");
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-modal", "true");
  sheet.setAttribute("aria-labelledby", "bulk-recipe-title");

  const header = el("div", "sheet-header");
  const row = el("div", "sheet-title-row");
  const title = el("span", "sheet-title", sheetTitle(field, count), "bulk-recipe-title");
  title.id = "bulk-recipe-title";
  const x = el("button", "btn-icon", "✕", "bulk-recipe-close");
  x.type = "button";
  x.setAttribute("aria-label", "Close");
  row.append(title, x);
  header.append(row);

  const body = el("div", "bv-body", undefined, "bulk-recipe-body");
  const foot = el("div", "sheet-footer bv-footer");
  const note = el("p", "admin-field-help bv-reason", "", "bulk-recipe-reason");
  const buttons = el("div", "bv-buttons");
  const cancel = el("button", "btn-secondary", BULK_RECIPE_COPY.cancel, "bulk-recipe-cancel");
  cancel.type = "button";
  const primary = el("button", "btn-primary", BULK_RECIPE_COPY[field], "bulk-recipe-apply");
  primary.type = "button";
  buttons.append(cancel, primary);
  foot.append(note, buttons);
  sheet.append(header, body, foot);
  root.append(sheet);
  scrim = root;

  // The picks. Which of these a field uses: category = pickedValue; active = pickedBinary; requiresLiquor = pickedBinary plus
  // pickedLiquor when On; buildCharge = chargeText.
  let pickedValue: string | undefined;
  let pickedBinary: boolean | undefined;
  const pickedLiquor = new Set<string>();
  let chargeText = "";
  let chargeError = "";
  let busy = false;
  let finished = false;

  const alive = (): boolean => generation === mine && scrim === root;
  x.addEventListener("click", () => {
    if (!busy) closeBulkRecipeSheet();
  });
  cancel.addEventListener("click", () => {
    if (!busy) closeBulkRecipeSheet();
  });
  root.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !busy) closeBulkRecipeSheet();
  });

  const labelOf = (value: string): string => opts.options.find((option) => option.value === value)?.label ?? value;

  function chargeCents(): number | undefined {
    if (chargeText.trim() === "") return undefined;
    try {
      return parseDollarsToCentsExact(chargeText, "Build charge");
    } catch {
      return undefined;
    }
  }

  /** The choice as the sentences want it, or undefined while the pick is not complete. */
  function choice(): BulkRecipeChoice | undefined {
    switch (field) {
      case "category":
        return pickedValue === undefined ? undefined : { field, categoryLabel: labelOf(pickedValue) };
      case "active":
        return pickedBinary === undefined ? undefined : { field, active: pickedBinary };
      case "requiresLiquor": {
        if (pickedBinary === undefined) return undefined;
        if (!pickedBinary) return { field, requires: false };
        if (pickedLiquor.size === 0) return undefined;
        return {
          field,
          requires: true,
          liquorLabels: opts.options.filter((option) => pickedLiquor.has(option.value)).map((option) => option.label),
        };
      }
      case "buildCharge": {
        const cents = chargeCents();
        return cents === undefined ? undefined : { field, chargeCents: cents };
      }
    }
  }

  function request(): BulkRecipeRequest | undefined {
    switch (field) {
      case "category":
        return pickedValue === undefined ? undefined : { field, category: pickedValue };
      case "active":
        return pickedBinary === undefined ? undefined : { field, active: pickedBinary };
      case "requiresLiquor":
        if (pickedBinary === undefined) return undefined;
        if (!pickedBinary) return { field, requiresLiquorSelection: false };
        return pickedLiquor.size === 0
          ? undefined
          : {
              field,
              requiresLiquorSelection: true,
              liquorSlots: opts.options.filter((option) => pickedLiquor.has(option.value)).map((option) => option.value),
            };
      case "buildCharge": {
        const cents = chargeCents();
        return cents === undefined ? undefined : { field, buildChargeCents: cents };
      }
    }
  }

  function reason(): string {
    if (field === "requiresLiquor" && pickedBinary === true && pickedLiquor.size === 0) {
      return BULK_RECIPE_COPY.pickLiquorCategory;
    }
    if (field === "buildCharge") return BULK_RECIPE_COPY.enterCharge;
    return BULK_RECIPE_COPY.pickOne;
  }

  function paintFooter(): void {
    const picked = choice();
    primary.disabled = busy || picked === undefined;
    primary.textContent = picked === undefined ? BULK_RECIPE_COPY[field] : applyButtonLabel(picked, count);
    note.textContent = picked === undefined && !busy && !finished ? reason() : "";
    cancel.disabled = busy;
  }

  function radioList(
    name: string,
    label: string,
    entries: readonly { key: string; label: string; checked: boolean; pick: () => void }[],
  ): HTMLElement {
    const list = el("div", "bv-vendors", undefined, "bulk-recipe-list");
    list.setAttribute("role", "radiogroup");
    list.setAttribute("aria-label", label);
    for (const entry of entries) {
      const labelEl = el("label", "bv-vendor", undefined, "bulk-recipe-option");
      const input = el("input");
      input.type = "radio";
      input.name = name;
      input.value = entry.key;
      input.checked = entry.checked;
      input.dataset["testid"] = "bulk-recipe-radio";
      input.addEventListener("change", () => {
        entry.pick();
        paintPick();
      });
      labelEl.append(input, el("span", "bv-vendor-name", entry.label));
      list.append(labelEl);
    }
    return list;
  }

  function paintPick(): void {
    body.replaceChildren();
    if ((field === "category" || field === "requiresLiquor") && opts.options.length === 0) {
      body.append(
        el(
          "p",
          "tabs-empty",
          field === "category" ? BULK_RECIPE_COPY.noCategories : BULK_RECIPE_COPY.noLiquorCategories,
          "bulk-recipe-empty",
        ),
      );
      if (field === "category") {
        paintFooter();
        primary.disabled = true;
        return;
      }
    }
    if (field === "category") {
      body.append(
        radioList(
          "bulk-recipe-choice",
          BULK_RECIPE_COPY.category,
          opts.options.map((option) => ({
            key: option.value,
            label: option.label,
            checked: option.value === pickedValue,
            pick: () => {
              pickedValue = option.value;
            },
          })),
        ),
      );
    } else if (field === "active" || field === "requiresLiquor") {
      const [onLabel, offLabel] =
        field === "active"
          ? [BULK_RECIPE_COPY.activeWord, BULK_RECIPE_COPY.inactiveWord]
          : [BULK_RECIPE_COPY.onWord, BULK_RECIPE_COPY.offWord];
      body.append(
        radioList(
          "bulk-recipe-choice",
          BULK_RECIPE_COPY[field],
          [
            { key: "on", label: onLabel, checked: pickedBinary === true, pick: () => (pickedBinary = true) },
            { key: "off", label: offLabel, checked: pickedBinary === false, pick: () => (pickedBinary = false) },
          ],
        ),
      );
      if (field === "requiresLiquor" && pickedBinary === true) {
        const group = el("div", "bv-vendors", undefined, "bulk-recipe-liquor-list");
        group.setAttribute("role", "group");
        group.setAttribute("aria-label", BULK_RECIPE_COPY.liquorCategoriesLabel);
        body.append(el("p", "bv-confirm", `${BULK_RECIPE_COPY.liquorCategoriesLabel}`));
        for (const option of opts.options) {
          const labelEl = el("label", "bv-vendor", undefined, "bulk-recipe-liquor-option");
          const input = el("input");
          input.type = "checkbox";
          input.value = option.value;
          input.checked = pickedLiquor.has(option.value);
          input.dataset["testid"] = "bulk-recipe-liquor-check";
          input.addEventListener("change", () => {
            if (input.checked) pickedLiquor.add(option.value);
            else pickedLiquor.delete(option.value);
            paintFooter();
            paintConfirm();
          });
          labelEl.append(input, el("span", "bv-vendor-name", option.label));
          group.append(labelEl);
        }
        body.append(group);
      }
    } else {
      const label = el("label", "field-label", BULK_RECIPE_COPY.buildChargeLabel);
      label.htmlFor = "bulk-recipe-charge";
      const affix = el("span", "field-input-affix field-input-affix--prefix");
      const mark = el("span", "field-input-affix-mark", "$");
      mark.setAttribute("aria-hidden", "true");
      const input = el("input", "field-input", undefined, "bulk-recipe-charge");
      input.id = "bulk-recipe-charge";
      input.type = "number";
      input.inputMode = "decimal";
      input.min = "0";
      input.step = "0.01";
      input.value = chargeText;
      input.addEventListener("input", () => {
        chargeText = input.value;
        chargeError = "";
        if (chargeText.trim() !== "") {
          try {
            parseDollarsToCentsExact(chargeText, "Build charge");
          } catch (error) {
            chargeError = error instanceof Error ? error.message : "Enter a valid build charge.";
          }
        }
        errorEl.textContent = chargeError;
        paintFooter();
        paintConfirm();
      });
      affix.append(mark, input);
      const errorEl = el("p", "admin-form-error", chargeError, "bulk-recipe-charge-error");
      body.append(label, affix, errorEl);
    }
    body.append(el("div", undefined, undefined, "bulk-recipe-confirm-slot"));
    paintConfirm();
    paintFooter();
  }

  function paintConfirm(): void {
    const slot = body.querySelector<HTMLElement>("[data-testid='bulk-recipe-confirm-slot']");
    if (slot === null) return;
    slot.replaceChildren();
    const picked = choice();
    if (picked !== undefined) slot.append(el("p", "bv-confirm", confirmSentence(picked, count), "bulk-recipe-confirm"));
  }

  function paintResult(totals: BulkRecipeTotals, stopped: string): void {
    finished = true;
    body.replaceChildren();
    const sentence = resultSentence(totals);
    const good = totals.failed.length === 0 && stopped === "";
    const head = el(
      "p",
      good ? "bv-result bv-result--ok" : "bv-result bv-result--warn",
      `${good ? "✓ " : "! "}${sentence}`,
      "bulk-recipe-result",
    );
    head.setAttribute("role", "status");
    body.append(head);
    if (stopped !== "") body.append(el("p", "bv-result bv-result--warn", `! ${stopped}`, "bulk-recipe-stopped"));
    if (totals.failed.length > 0) {
      const list = el("ul", "bv-failed", undefined, "bulk-recipe-failed");
      for (const failure of totals.failed) {
        const item = el("li", undefined, undefined, "bulk-recipe-failed-item");
        // The server cannot name a drink that no longer exists; the screen still knows the name it listed.
        const shownName = opts.drinks.find((drink) => drink.id === failure.menuDrinkId)?.name ?? failure.drinkName;
        item.append(el("strong", undefined, shownName), ` ${failure.reason}`);
        list.append(item);
      }
      body.append(list);
    }
    closeOutcome = { touched: true, failedIds: totals.failed.map((failure) => failure.menuDrinkId) };
    buttons.replaceChildren();
    if (stopped !== "") {
      cancel.disabled = false;
      primary.disabled = false;
      const retry = el("button", "btn-secondary", BULK_RECIPE_COPY[field], "bulk-recipe-resume");
      retry.type = "button";
      retry.addEventListener("click", () => {
        finished = false;
        buttons.replaceChildren(cancel, primary);
        paintPick();
      });
      const done = el("button", "btn-primary", BULK_RECIPE_COPY.done, "bulk-recipe-done");
      done.type = "button";
      done.addEventListener("click", () => closeBulkRecipeSheet());
      buttons.append(retry, done);
    } else {
      const done = el("button", "btn-primary", BULK_RECIPE_COPY.done, "bulk-recipe-done");
      done.type = "button";
      done.addEventListener("click", () => closeBulkRecipeSheet());
      buttons.append(done);
      done.focus();
    }
    note.textContent = "";
  }

  async function apply(): Promise<void> {
    const body_ = request();
    if (busy || body_ === undefined) return;
    busy = true;
    const batches = toBatches(
      opts.drinks.map((drink) => drink.id),
      BULK_RECIPE_BATCH_SIZE,
    );
    let totals = emptyTotals();
    let doneCount = 0;
    let stopped = "";
    for (const batch of batches) {
      paintFooter();
      body.replaceChildren(
        el("p", "bv-progress", `${BULK_RECIPE_COPY.applying}: ${doneCount} of ${count}`, "bulk-recipe-progress"),
      );
      const answer = await opts.applyBatch(body_, batch);
      if (!alive()) return;
      closeOutcome = { touched: true, failedIds: closeOutcome.failedIds };
      if (!answer.ok) {
        stopped = stoppedSentence(doneCount, count, answer.message);
        break;
      }
      totals = addBatch(totals, answer.result);
      doneCount += batch.length;
    }
    busy = false;
    paintResult(totals, stopped);
  }
  primary.addEventListener("click", () => void apply());

  document.body.append(root);
  paintPick();
  cancel.focus();
}
